/**
 * Eat the click that follows the press which dismissed something floating.
 *
 * A menu, a popover and the account card all close on a press outside them, and a press outside
 * them is also a press on whatever happens to be there. Left alone, one gesture does two jobs:
 * the thing closes and the row underneath it opens, which is never what was meant — the press
 * was aimed at getting rid of what was in the way.
 *
 * The dismiss happens on the pointer going down and the activation on the click that follows
 * it, so this takes the click out of the air between them. Captured on the window, so it is the
 * first thing to see the event and nothing downstream — React's own delegated handlers
 * included — ever does. The timer is the way out for a pointer that goes down and never comes
 * back up as a click at all: a drag, a scroll, a press that wandered off the page.
 */
export function swallowNextClick(): void {
  /*
   * Held rather than looked up again when the timer comes round. The window a listener was
   * put on is the window it has to come off, and a callback that reaches for the global
   * three hundred milliseconds later can find there is no longer one there to reach for —
   * which is a thrown error out of a timer nobody is waiting on.
   */
  const view = window
  const options: AddEventListenerOptions = { capture: true }
  let waiting = 0
  const swallow = (event: MouseEvent) => {
    event.stopPropagation()
    event.preventDefault()
    // The click came, so the way out for the click that never comes is not needed.
    view.clearTimeout(waiting)
    view.removeEventListener('click', swallow, options)
  }
  view.addEventListener('click', swallow, options)
  waiting = view.setTimeout(() => {
    view.removeEventListener('click', swallow, options)
  }, SWALLOW_MS)
}

/** How long a click is waited for before the press is taken to have been something else. */
const SWALLOW_MS = 300
